import { gqlFetcher, graphql } from '#features/api';
import { getCallClientSessionId } from '#features/calls/utils/nativeCalls';

export type CallSessionPayload = {
  id: string;
  chatId: string;
  callerId: string;
  calleeId: string;
  peerUserId: string;
  status: string;
  roomName: string;
  mediaType: string | null;
  endReason: string | null;
  /** ISO instant the call row was created — i.e. when the phone started ringing. */
  createdAt: string | null;
  livekitUrl: string | null;
  token: string | null;
  /**
   * Клиент, которому звонок принадлежит сейчас. Пустой `token` при непустом `ownerSession` значит
   * «звонок открыт на другом устройстве»: подключаться нельзя, можно предложить перенести.
   */
  ownerSession: string | null;
};

/** Downstream error code (call.busy, call.rate.limited, …) preserved for UI mapping. */
export class CallRequestError extends Error {
  readonly errorCode: string;

  constructor(errorCode: string) {
    super(errorCode);
    this.errorCode = errorCode;
  }
}

const CALL_SESSION_FIELDS = `
  id
  chatId
  callerId
  calleeId
  peerUserId
  status
  roomName
  mediaType
  endReason
  createdAt
  livekitUrl
  token
  ownerSession
`;

const startCallDoc = graphql(`
  mutation CallStart($peerUserId: UUID!, $video: Boolean, $sessionId: String) {
    call {
      start(peerUserId: $peerUserId, video: $video, sessionId: $sessionId) {
        __typename
        ... on CallSession {
          id
          chatId
          callerId
          calleeId
          peerUserId
          status
          roomName
          mediaType
          endReason
          createdAt
          livekitUrl
          token
          ownerSession
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

const answerCallDoc = graphql(`
  mutation CallAnswer($callId: UUID!, $sessionId: String) {
    call {
      answer(callId: $callId, sessionId: $sessionId) {
        __typename
        ... on CallSession {
          id
          chatId
          callerId
          calleeId
          peerUserId
          status
          roomName
          mediaType
          endReason
          createdAt
          livekitUrl
          token
          ownerSession
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

const hangupCallDoc = graphql(`
  mutation CallHangup($callId: UUID!) {
    call {
      hangup(callId: $callId) {
        __typename
        ... on CallSession {
          id
          chatId
          callerId
          calleeId
          peerUserId
          status
          roomName
          mediaType
          endReason
          createdAt
          livekitUrl
          token
          ownerSession
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

/*
  Тот же отбой, но с причиной. Отдельный документ, а не необязательная переменная в прежнем:
  аргумент, которого нет в схеме шлюза, валит весь запрос ещё на проверке документа, и обычный
  отбой не должен зависеть от того, выкачен ли уже шлюз с `reason`.
*/
const hangupCallWithReasonDoc = graphql(`
  mutation CallHangupWithReason($callId: UUID!, $reason: String) {
    call {
      hangup(callId: $callId, reason: $reason) {
        __typename
        ... on CallSession {
          id
          chatId
          callerId
          calleeId
          peerUserId
          status
          roomName
          mediaType
          endReason
          createdAt
          livekitUrl
          token
          ownerSession
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

// Plain string on purpose: `call.active` returns the bare CallSession type (no union),
// mirroring the chatMessagesQuery escape hatch used elsewhere in this folder family.
const activeCallQuery = `
  query CallActive($sessionId: String) {
    call {
      active(sessionId: $sessionId) {
        ${CALL_SESSION_FIELDS}
      }
    }
  }
`;

const claimCallDoc = graphql(`
  mutation CallClaim($callId: UUID!, $sessionId: String!) {
    call {
      claim(callId: $callId, sessionId: $sessionId) {
        __typename
        ... on CallSession {
          id
          chatId
          callerId
          calleeId
          peerUserId
          status
          roomName
          mediaType
          endReason
          createdAt
          livekitUrl
          token
          ownerSession
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

type CallUnionPayload =
  | ({ __typename: 'CallSession' } & CallSessionPayload)
  | { __typename: 'GlobalError'; errorCode?: string | null; errorMessage?: string | null }
  | { __typename?: string };

const unwrapSession = (payload: CallUnionPayload | null | undefined): CallSessionPayload => {
  if (!payload || payload.__typename !== 'CallSession') {
    const errorCode =
      payload && payload.__typename === 'GlobalError'
        ? ((payload as { errorCode?: string | null }).errorCode ?? 'global.error')
        : 'global.error';
    throw new CallRequestError(errorCode);
  }
  return payload as CallSessionPayload;
};

export const startCall = async (
  peerUserId: string,
  video: boolean
): Promise<CallSessionPayload> => {
  const data = (await gqlFetcher(startCallDoc, {
    peerUserId,
    video,
    sessionId: await getCallClientSessionId(),
  })) as {
    call?: { start?: CallUnionPayload | null } | null;
  };
  return unwrapSession(data.call?.start);
};

export const answerCall = async (callId: string): Promise<CallSessionPayload> => {
  const data = (await gqlFetcher(answerCallDoc, {
    callId,
    sessionId: await getCallClientSessionId(),
  })) as {
    call?: { answer?: CallUnionPayload | null } | null;
  };
  return unwrapSession(data.call?.answer);
};

/**
 * Почему клиент сам кладёт трубку. Сервер принимает только эти значения (остальное читает как
 * обычный отбой): `peer_never_joined` - собеседник так и не вошёл в комнату, карточка в переписке
 * скажет "Не соединился"; `peer_gone` - был и ушёл, карточка обычная, причина нужна для разбора.
 */
export type CallHangupReason = 'peer_never_joined' | 'peer_gone';

type HangupResponse = { call?: { hangup?: CallUnionPayload | null } | null };

/**
 * Отбой с причиной, а если шлюз его отверг (старый шлюз не знает `reason` и валит весь документ),
 * - обычный отбой: звонок обязан закрыться, причина нужна только для разбора. Упал и повтор -
 * наверх уходит исходная ошибка.
 */
const sendHangupWithReason = async (
  callId: string,
  reason: CallHangupReason
): Promise<HangupResponse> => {
  try {
    return (await gqlFetcher(hangupCallWithReasonDoc, { callId, reason })) as HangupResponse;
  } catch (error) {
    try {
      return (await gqlFetcher(hangupCallDoc, { callId })) as HangupResponse;
    } catch {
      throw error;
    }
  }
};

export const hangupCall = async (
  callId: string,
  reason?: CallHangupReason
): Promise<CallSessionPayload> => {
  const data = reason
    ? await sendHangupWithReason(callId, reason)
    : ((await gqlFetcher(hangupCallDoc, { callId })) as HangupResponse);
  return unwrapSession(data.call?.hangup);
};

// Resync after a reload knows only the peer's id — pull their display identity
// from the chat (userData arrives with a fully resolved avatar URL).
const callChatPeerQuery = `
  query CallChatPeer($id: UUID!) {
    chat {
      byId(id: $id) {
        userId
        userData {
          displayName
          avatar
        }
      }
    }
  }
`;

export const fetchCallChatPeer = async (
  chatId: string
): Promise<{ displayName: string | null; avatar: string | null } | null> => {
  const data = (await gqlFetcher(callChatPeerQuery, { id: chatId })) as {
    chat?: {
      byId?: {
        userData?: { displayName?: string | null; avatar?: string | null } | null;
      } | null;
    } | null;
  };
  const userData = data.chat?.byId?.userData;
  if (!userData) {
    return null;
  }
  return { displayName: userData.displayName ?? null, avatar: userData.avatar ?? null };
};

/** Перенести живой звонок на этот клиент — другое устройство отпустит комнату. */
export const claimCall = async (callId: string): Promise<CallSessionPayload> => {
  const data = (await gqlFetcher(claimCallDoc, {
    callId,
    sessionId: await getCallClientSessionId(),
  })) as {
    call?: { claim?: CallUnionPayload | null } | null;
  };
  return unwrapSession(data.call?.claim);
};

export const fetchActiveCall = async (): Promise<CallSessionPayload | null> => {
  const data = (await gqlFetcher(activeCallQuery, {
    sessionId: await getCallClientSessionId(),
  })) as {
    call?: { active?: CallSessionPayload | null } | null;
  };
  return data.call?.active ?? null;
};
